import { MEDIA } from "@/lib/media";

export const STORY_STEPS = [
  {
    kicker: "The Problem",
    title: "Landscape degradation",
    body: "Across Australia, ecosystems are unravelling. Cleared land, salinity, weeds and fire have left millions of hectares in need of repair.",
    image: MEDIA.degraded,
  },
  {
    kicker: "The Opening",
    title: "Restoration opportunity",
    body: "We have a once-in-a-generation chance to restore Country at scale — through carbon projects, mine rehabilitation, Landcare and Traditional Owner stewardship.",
    image: "https://images.unsplash.com/photo-1694230896211-f990ae81163e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    kicker: "The Constraint",
    title: "Native seed shortage",
    body: "Restoration runs on seed. Yet Australia faces a chronic shortage of quality, provenance native seed — the single greatest bottleneck to repair.",
    image: MEDIA.seedPalm,
  },
  {
    kicker: "The Missing Piece",
    title: "A need for skilled people",
    body: "Seed doesn't collect, clean, store or sow itself. We need thousands of trained practitioners — collectors, processors, enterprise leaders.",
    image: MEDIA.handsSeed,
  },
  {
    kicker: "The Practice",
    title: "Stewardship",
    body: "Beyond technique lies stewardship: an enduring relationship with place, people and future generations. This is the deeper skill the Academy teaches.",
    image: MEDIA.plantingWoman,
  },
  {
    kicker: "The Horizon",
    title: "Hope",
    body: "With knowledge, people and leadership in place, Australia's landscapes can be restored — for generations to come. This is the future we are building.",
    image: MEDIA.restored,
  },
];

export const BEFORE = [
  "Interested in restoration",
  "Collecting casually",
  "Watching YouTube",
  "Unsure where to start",
  "Working alone",
];

export const AFTER = [
  "Professional practitioner",
  "Community leader",
  "Seed bank manager",
  "Restoration specialist",
  "Seed enterprise owner",
  "Providing fee-for-service on-Country work",
  "Connected to Australia's restoration network",
];

export const ACCELERATOR_WEEKS = [
  { week: "01–02", title: "Foundations & Country", desc: "Reading landscapes, ecological literacy, and the principles of ethical, provenance-based practice." },
  { week: "03–04", title: "Professional Collection", desc: "Field technique, timing, documentation and quality — collecting like a professional." },
  { week: "05–06", title: "Processing & Banking", desc: "Cleaning, drying, viability testing and long-term storage that protects diversity." },
  { week: "07–08", title: "Supply & Enterprise", desc: "The restoration supply chain, community seed banks and sustainable enterprise models." },
  { week: "09–10", title: "Monitoring & Documentation", desc: "Field documentation, monitoring and measuring restoration outcomes over time." },
  { week: "11", title: "Capstone Design", desc: "Design a real restoration seed plan for a place that matters to you." },
  { week: "12", title: "Certification & Community", desc: "Professional certification, presentation and joining Australia's restoration network." },
];

export const ACCELERATOR_INCLUDES = [
  "Weekly live mentoring",
  "Field demonstrations",
  "Practical projects",
  "Professional certification",
  "National community",
  "Capstone project",
];

export const PROJECTS = [
  { title: "Professional Field Kit", desc: "Assemble and master the tools of a working seed practitioner.", image: MEDIA.handsSeed, outcome: "A field-ready kit and workflow you use for real collections." },
  { title: "Seed Collection Workflow", desc: "Design a documented, provenance-based collection process.", image: MEDIA.seedPalm, outcome: "A repeatable, auditable collection system." },
  { title: "Home Seed Bank", desc: "Build a small, well-documented seed bank at home.", image: MEDIA.seedProcess, outcome: "A living seed bank protecting local diversity." },
  { title: "Restoration Seed Plan", desc: "Plan species, quantities and timing for a real site.", image: MEDIA.nursery, outcome: "A professional plan ready to hand to a project." },
  { title: "Business Plan", desc: "Design a viable seed enterprise or service.", image: MEDIA.seedTrays, outcome: "A costed, credible enterprise plan." },
  { title: "Community Seed Bank", desc: "Blueprint governance and systems for a shared bank.", image: "https://customer-assets.emergentagent.com/job_academy-2/artifacts/67s6tt6s_seedkeepers%2013.webp", outcome: "A launch-ready community seed bank model." },
  { title: "Capstone Restoration Project", desc: "Bring it all together on a place that matters.", image: MEDIA.restored, outcome: "A completed, portfolio-grade restoration project." },
];

export const STEWARD_NODES = ["People", "Country", "Plants", "Culture", "Knowledge", "Future generations"];

export const AI_USES = [
  { title: "Species identification", desc: "Identify plants and seed from the field in seconds." },
  { title: "Collection planning", desc: "Optimise timing, provenance zones and routes." },
  { title: "Grant writing", desc: "Draft compelling, evidence-based funding proposals." },
  { title: "Business planning", desc: "Model enterprises, costs and supply forecasts." },
  { title: "Proposal writing", desc: "Turn field knowledge into persuasive documents." },
  { title: "Knowledge management", desc: "Organise notes, records and institutional memory." },
  { title: "Restoration calculations", desc: "Estimate seeding rates, areas and quantities." },
  { title: "Field documentation", desc: "Capture and structure observations effortlessly." },
  { title: "Monitoring", desc: "Track outcomes and detect change over time." },
];

export const COMMUNITY_FEATURES = [
  "Regional learning circles", "Mentors", "Live workshops", "Discussion groups",
  "Field days", "Annual gathering", "Job opportunities", "Research updates",
  "Seed exchanges", "Grant alerts", "Marketplace", "Member stories",
];

export const REGIONS = [
  { name: "Kimberley", x: 26, y: 20 },
  { name: "Top End", x: 45, y: 12 },
  { name: "Cape York", x: 66, y: 15 },
  { name: "Central Desert", x: 47, y: 42 },
  { name: "South West", x: 16, y: 62 },
  { name: "Murray–Darling", x: 60, y: 58 },
  { name: "Great Southern", x: 22, y: 74 },
  { name: "South East", x: 68, y: 72 },
  { name: "Tasmania", x: 66, y: 90 },
];

export const CAREERS = [
  "Traditional Owner organisations", "Ranger programs", "Mining rehabilitation",
  "Environmental consulting", "Landcare", "Government", "Local councils",
  "Bush regeneration", "Native nurseries", "Carbon projects", "Community seed enterprises",
];

export const FUTURE_ARCHITECTURE = [
  "Professional certification", "Memberships", "Learning dashboard", "Student profiles",
  "Digital credentials", "Mentor matching", "National conference", "Research centre",
  "Podcast", "Mobile app", "Regional field schools", "Interactive species database",
  "Marketplace", "Jobs board", "Scholarships", "Industry partnerships",
];
